import * as React from 'react';
import EchoCard from '@/components/common/EchoCard';
import { useEchoStatsQuery } from '@/hooks/useEchoStatsQuery';
import { formatCompactNumber } from '@/utils/formatters';
import { getEchoDomain } from '@/utils/echoUtils';
import type { Echo } from '@/types';

interface SmartEchoCardProps {
  echo: Echo;
  imageSrc: string;
  borderColor?: string;
  onClick?: () => void;
  onAction?: () => void;
  className?: string;
  imageContainerClassName?: string;
  panelClassName?: string;
  titleClassName?: string;
  hideBadges?: boolean;
  badgeContainerClassName?: string;
  starBadgeClassName?: string;
  fansBadgeClassName?: string;
  imageClassName?: string;
  glowClassName?: string;
  disableHover?: boolean;
  hidePanel?: boolean;
  showTextOverlay?: boolean;
}

/**
 * SmartEchoCard
 * Wraps the presentational EchoCard to inject real-time stats via React Query.
 * Keeps the UI component "dumb" and handles fetching/formatting in the container.
 */
export const SmartEchoCard: React.FC<SmartEchoCardProps> = ({
  echo,
  imageSrc,
  borderColor,
  onClick,
  onAction,
  className,
  imageContainerClassName,
  panelClassName,
  titleClassName,
  hideBadges,
  badgeContainerClassName,
  starBadgeClassName,
  fansBadgeClassName,
  imageClassName,
  glowClassName,
  disableHover,
  hidePanel,
  showTextOverlay,
}) => {
  const { data } = useEchoStatsQuery(echo.id);

  // Use super likes for stars count
  const formattedLikes = formatCompactNumber(data?.total_super_likes ?? 0);
  const formattedFans = formatCompactNumber(data?.total_fans ?? 0);

  const displayEcho = React.useMemo(
    () => ({
      ...echo,
      category: getEchoDomain(echo),
    }),
    [echo]
  );

  return (
    <EchoCard
      echo={displayEcho}
      imageSrc={imageSrc}
      {...(borderColor !== undefined ? { borderColor } : {})}
      {...(onClick ? { onClick } : {})}
      {...(onAction ? { onAction } : {})}
      variant='default'
      starsCount={formattedLikes}
      fansCount={formattedFans}
      {...(className !== undefined ? { className } : {})}
      {...(imageContainerClassName !== undefined ? { imageContainerClassName } : {})}
      {...(panelClassName !== undefined ? { panelClassName } : {})}
      {...(titleClassName !== undefined ? { titleClassName } : {})}
      {...(hideBadges !== undefined ? { hideBadges } : {})}
      {...(badgeContainerClassName !== undefined ? { badgeContainerClassName } : {})}
      {...(starBadgeClassName !== undefined ? { starBadgeClassName } : {})}
      {...(fansBadgeClassName !== undefined ? { fansBadgeClassName } : {})}
      {...(imageClassName !== undefined ? { imageClassName } : {})}
      {...(glowClassName !== undefined ? { glowClassName } : {})}
      {...(disableHover !== undefined ? { disableHover } : {})}
      {...(hidePanel !== undefined ? { hidePanel } : {})}
      {...(showTextOverlay !== undefined ? { showTextOverlay } : {})}
    />
  );
};

export default SmartEchoCard;
